/* ============================================
   ArtifactPanel — Modal overlay for strategy docs
   Opens as a centered modal with dark backdrop.
   ============================================ */
import { useState } from 'react'
import type { ChatArtifact } from '@/lib/types'

interface ArtifactPanelProps {
  artifact: ChatArtifact
  onClose: () => void
  onApprove: (artifact: ChatArtifact) => void
  onRequestChange: (feedback: string) => void
}

const PILLAR_COLORS = [
  { bg: 'color-mix(in srgb, var(--primary-light) 15%, transparent)', border: 'color-mix(in srgb, var(--primary-light) 30%, transparent)', text: '#7ba4f7' },
  { bg: 'rgba(139, 92, 246, 0.15)', border: 'color-mix(in srgb, var(--primary-light) 30%, transparent)', text: '#b794f6' },
  { bg: 'color-mix(in srgb, var(--cat-7) 15%, transparent)', border: 'color-mix(in srgb, var(--cat-7) 30%, transparent)', text: '#f472b6' },
  { bg: 'color-mix(in srgb, var(--warning) 15%, transparent)', border: 'color-mix(in srgb, var(--warning) 30%, transparent)', text: '#fbbf24' },
  { bg: 'rgba(16, 185, 129, 0.15)', border: 'color-mix(in srgb, var(--success) 30%, transparent)', text: '#6ee7b7' },
  { bg: 'color-mix(in srgb, var(--cat-7) 15%, transparent)', border: 'color-mix(in srgb, var(--cat-7) 30%, transparent)', text: '#fb7185' },
]

const ESTADO_CONFIG: Record<ChatArtifact['estado'], { label: string; className: string }> = {
  draft:    { label: 'Borrador', className: 'marco-artifact__badge--draft' },
  approved: { label: 'Aprobado', className: 'marco-artifact__badge--approved' },
  applied:  { label: 'Aplicado', className: 'marco-artifact__badge--applied' },
}

const TIPO_LABELS: Record<ChatArtifact['tipo'], string> = {
  strategy: 'Estrategia',
  calendar: 'Calendario',
  brief: 'Brief',
}

export default function ArtifactPanel({
  artifact,
  onClose,
  onApprove,
  onRequestChange,
}: ArtifactPanelProps) {
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState('')

  const { contenido, version, estado, titulo, tipo } = artifact
  const estadoCfg = ESTADO_CONFIG[estado]

  function handleSubmitFeedback(): void {
    const trimmed = feedback.trim()
    if (!trimmed) return
    onRequestChange(trimmed)
    setFeedback('')
    setShowFeedback(false)
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmitFeedback()
    }
    if (e.key === 'Escape') {
      if (showFeedback) { setShowFeedback(false); setFeedback('') }
      else onClose()
    }
  }

  return (
    <div className="marco-artifact-overlay" onClick={onClose}>
      <div className="marco-artifact-modal" onClick={(e) => e.stopPropagation()}>
        {/* ─── Header ─── */}
        <div className="marco-artifact__header">
          <div className="marco-artifact__header-left">
            <div className="marco-artifact__doc-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <div className="marco-artifact__title-group">
              <h3 className="marco-artifact__title">{titulo}</h3>
              <span className="marco-artifact__tipo">{TIPO_LABELS[tipo]}</span>
            </div>
          </div>
          <div className="marco-artifact__header-right">
            <span className="marco-artifact__badge marco-artifact__badge--version">
              v{version}
            </span>
            <span className={`marco-artifact__badge ${estadoCfg.className}`}>
              {estadoCfg.label}
            </span>
            <button className="marco-artifact__close" onClick={onClose} aria-label="Cerrar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ─── Body ─── */}
        <div className="marco-artifact__body">

          {/* Voz de Marca */}
          <section className="marco-artifact__section">
            <div className="marco-artifact__section-header">
              <div className="marco-artifact__section-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
              </div>
              <h4 className="marco-artifact__section-title">Voz de Marca</h4>
            </div>
            {contenido.voz_marca ? (
              <div className="marco-artifact__quote">
                <span className="marco-artifact__quote-mark">"</span>
                <p className="marco-artifact__quote-text">{contenido.voz_marca}</p>
                <span className="marco-artifact__quote-mark marco-artifact__quote-mark--end">"</span>
              </div>
            ) : (
              <p className="marco-artifact__empty">Sin definir aún</p>
            )}
          </section>

          <div className="marco-artifact__divider" />

          {/* Audiencia Objetivo */}
          <section className="marco-artifact__section">
            <div className="marco-artifact__section-header">
              <div className="marco-artifact__section-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h4 className="marco-artifact__section-title">Audiencia Objetivo</h4>
            </div>
            {contenido.audiencia_objetivo ? (
              <p className="marco-artifact__text">{contenido.audiencia_objetivo}</p>
            ) : (
              <p className="marco-artifact__empty">Sin definir aún</p>
            )}
          </section>

          <div className="marco-artifact__divider" />

          {/* Pilares de Contenido */}
          <section className="marco-artifact__section">
            <div className="marco-artifact__section-header">
              <div className="marco-artifact__section-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>
                </svg>
              </div>
              <h4 className="marco-artifact__section-title">Pilares de Contenido</h4>
            </div>
            {contenido.pilares_contenido && contenido.pilares_contenido.length > 0 ? (
              <div className="marco-artifact__pills">
                {contenido.pilares_contenido.map((pilar: string, i: number) => {
                  const color = PILLAR_COLORS[i % PILLAR_COLORS.length]
                  return (
                    <span key={i} className="marco-artifact__pill" style={{ background: color.bg, borderColor: color.border, color: color.text }}>
                      {pilar}
                    </span>
                  )
                })}
              </div>
            ) : (
              <p className="marco-artifact__empty">Sin pilares definidos</p>
            )}
          </section>

          <div className="marco-artifact__divider" />

          {/* Hashtags */}
          <section className="marco-artifact__section">
            <div className="marco-artifact__section-header">
              <div className="marco-artifact__section-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/>
                </svg>
              </div>
              <h4 className="marco-artifact__section-title">Hashtags</h4>
            </div>
            {contenido.hashtags && contenido.hashtags.length > 0 ? (
              <div className="marco-artifact__pills">
                {contenido.hashtags.map((tag: string, i: number) => (
                  <span key={i} className="marco-artifact__pill marco-artifact__pill--hashtag">
                    #{tag.replace(/^#/, '')}
                  </span>
                ))}
              </div>
            ) : (
              <p className="marco-artifact__empty">Sin hashtags aún</p>
            )}
          </section>

          <div className="marco-artifact__divider" />

          {/* Estilo Visual */}
          <section className="marco-artifact__section">
            <div className="marco-artifact__section-header">
              <div className="marco-artifact__section-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="13.5" cy="6.5" r="2.5"/><path d="M17 21H7l6.5-12L17 15"/><path d="M2 21h20"/><path d="M7 21l-4-8"/>
                </svg>
              </div>
              <h4 className="marco-artifact__section-title">Estilo Visual</h4>
            </div>
            {contenido.estilo_visual ? (
              <p className="marco-artifact__text">{contenido.estilo_visual}</p>
            ) : (
              <p className="marco-artifact__empty">Sin definir aún</p>
            )}
          </section>
        </div>

        {/* ─── Footer ─── */}
        <div className="marco-artifact__footer">
          {showFeedback ? (
            <div className="marco-artifact__feedback">
              <input
                className="marco-artifact__feedback-input"
                type="text"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe los cambios que quieres..."
                autoFocus
              />
              <div className="marco-artifact__feedback-actions">
                <button className="marco-artifact__btn marco-artifact__btn--ghost" onClick={() => { setShowFeedback(false); setFeedback('') }}>
                  Cancelar
                </button>
                <button className="marco-artifact__btn marco-artifact__btn--primary" onClick={handleSubmitFeedback} disabled={!feedback.trim()}>
                  Enviar
                </button>
              </div>
            </div>
          ) : (
            <div className="marco-artifact__footer-buttons">
              <button className="marco-artifact__btn marco-artifact__btn--primary" onClick={() => onApprove(artifact)}>
                Aplicar estrategia
              </button>
              <button className="marco-artifact__btn marco-artifact__btn--ghost" onClick={() => setShowFeedback(true)}>
                Pedir cambios
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
